import { css, cx } from '@eti/styles';
import { Button, Inline, LinkButton, Stack, Text, textVariants, vars } from '@etg/wings';
import { ReactComponent as SeatIcon } from '@eti/assets/icons/Seat_thin.svg';
import { useTranslation } from '@eti/providers';
import { constants, price as priceUtils } from '@eti/utils';
import { useScrollActiveTravelerIntoView } from '../../hooks/useScrollActiveTravelerIntoView';
import { getTravelerButtonProps } from './travelerButtonUtils';
import { blackText, nonInteractiveTextStyles, removeButtonStyles, seatIconStyles, travelerCardStateStyles } from './styles';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const singleTravelerButtonStyles = {
  base: css`
    background: ${vars.colors.surface.primary};
    flex: 1;
    padding-inline: 8px;
    transition:
      background 0.3s ease,
      border-color 0.3s ease;

    & > span {
      width: 100%;
    }
  `,
  borderColor: travelerCardStateStyles
};
const nameText = css`
  color: ${vars.colors.text.primary};
  display: flex;
  overflow-wrap: break-word;
  text-align: left;
  white-space: normal;
  word-break: normal;
`;
const singleTravelerButtonContentStyles = css`
  justify-content: space-between;
  width: 100%;
`;
const blackTextButton = css`
  color: ${vars.colors.text.primary};
  overflow-wrap: break-word;
  text-align: left;
  white-space: normal;
  word-break: normal;
`;
const {
  MOUSE_FLOW_CLASS_NAMES
} = constants;
const TravelerName = ({
  name
}) => /*#__PURE__*/_jsx(Text, {
  className: cx('seatMap__travelerName', MOUSE_FLOW_CLASS_NAMES, nameText),
  variant: textVariants._14_BOLD,
  children: name
});
const SelectedSeatDetails = ({
  isRyanairInTrip,
  selectedSeat,
  travelerId,
  travelerName
}) => /*#__PURE__*/_jsxs(Stack, {
  align: "start",
  spacing: 8,
  children: [/*#__PURE__*/_jsx(TravelerName, {
    id: travelerId,
    name: travelerName
  }), /*#__PURE__*/_jsxs(Inline, {
    noWrap: true,
    spacing: 8,
    children: [!isRyanairInTrip && /*#__PURE__*/_jsx(Text, {
      className: blackTextButton,
      children: priceUtils.formatPrice(selectedSeat.price)
    }), /*#__PURE__*/_jsx(Text, {
      className: blackText,
      children: selectedSeat.value
    })]
  })]
});
const SeatActionText = ({
  isSingleTraveler,
  selectedSeat,
  showRemove
}) => {
  const {
    t
  } = useTranslation();
  const showRemoveButton = selectedSeat && (isSingleTraveler || showRemove);
  if (showRemoveButton || isSingleTraveler) {
    return null;
  }
  const text = selectedSeat ? t('Product.SeatMap.Change.Label') : t('Seating.SelectSeat.Header.Text');
  return /*#__PURE__*/_jsx("div", {
    className: nonInteractiveTextStyles,
    children: /*#__PURE__*/_jsx(Text, {
      "aria-label": text,
      variant: textVariants._14_BOLD,
      children: text
    })
  });
};
const NoSeatSelectedMessage = ({
  isSingleTraveler,
  selectedSeat,
  showRemove
}) => {
  const {
    t
  } = useTranslation();
  const showRemoveButton = selectedSeat && (isSingleTraveler || showRemove);
  if (showRemoveButton || !isSingleTraveler) {
    return null;
  }
  const text = t('Product.SeatMap.No.Selection.Label');
  return /*#__PURE__*/_jsx(Text, {
    "aria-label": text,
    variant: textVariants._14_BOLD,
    children: text
  });
};
const RemoveButton = ({
  activeSegment,
  isSingleTraveler,
  onRemoveSeat,
  selectedSeat,
  setTravelerButtonWithRemoveLink,
  showRemove,
  travelerId
}) => {
  const {
    t
  } = useTranslation();
  if (!selectedSeat) {
    return null;
  }
  const isRemoveButtonVisible = isSingleTraveler || showRemove;
  return /*#__PURE__*/_jsx(LinkButton, {
    className: cx({
      [removeButtonStyles.visible]: isRemoveButtonVisible,
      [removeButtonStyles.hidden]: !isRemoveButtonVisible
    }),
    onClick: e => {
      e.stopPropagation();
      onRemoveSeat(activeSegment, travelerId);
      setTravelerButtonWithRemoveLink(undefined);
    },
    children: t('Product.SeatMap.Summary.Seat.Remove')
  });
};
const SingleTravelerButton = ({
  activeSeatAssignments,
  activeSegment,
  activeTraveler,
  isRyanairInTrip,
  isSingleTraveler,
  onChange,
  onRemoveSeat,
  setTravelerButtonWithRemoveLink,
  traveler,
  travelerButtonWithRemoveLink
}) => {
  const selectedSeat = activeSeatAssignments === null || activeSeatAssignments === void 0 ? void 0 : activeSeatAssignments.find(s => String(s.id) === String(traveler.id));
  const showRemove = travelerButtonWithRemoveLink === traveler.id;
  const {
    handleClick,
    handleFocus,
    isActive
  } = getTravelerButtonProps({
    activeTraveler,
    onChange,
    selectedSeat,
    setTravelerButtonWithRemoveLink,
    travelerId: traveler.id
  });
  const travelerButtonRef = useScrollActiveTravelerIntoView(isActive);
  return /*#__PURE__*/_jsx(Button, {
    "aria-current": isActive ? 'step' : undefined,
    ref: travelerButtonRef,
    className: cx(singleTravelerButtonStyles.base, {
      [singleTravelerButtonStyles.borderColor.active]: isActive,
      [singleTravelerButtonStyles.borderColor.inactive]: !isActive
    }),
    "data-testid": `seatMapTravelerButton-${traveler.id}`,
    id: `seatMapTravelerButton-${traveler.id}`,
    onClick: handleClick,
    onFocus: handleFocus,
    children: /*#__PURE__*/_jsxs(Inline, {
      alignY: "top",
      className: singleTravelerButtonContentStyles,
      noWrap: true,
      spacing: 16,
      children: [/*#__PURE__*/_jsxs(Inline, {
        align: "start",
        noWrap: true,
        spacing: 4,
        children: [/*#__PURE__*/_jsx(SeatIcon, {
          className: seatIconStyles
        }), selectedSeat ? /*#__PURE__*/_jsx(SelectedSeatDetails, {
          isRyanairInTrip: isRyanairInTrip,
          selectedSeat: selectedSeat,
          travelerId: traveler.id,
          travelerName: traveler.name
        }) : /*#__PURE__*/_jsx(TravelerName, {
          id: traveler.id,
          name: traveler.name
        })]
      }), /*#__PURE__*/_jsx(SeatActionText, {
        isSingleTraveler: isSingleTraveler,
        selectedSeat: selectedSeat,
        showRemove: showRemove
      }), /*#__PURE__*/_jsx(NoSeatSelectedMessage, {
        isSingleTraveler: isSingleTraveler,
        selectedSeat: selectedSeat,
        showRemove: showRemove
      }), /*#__PURE__*/_jsx(RemoveButton, {
        activeSegment: activeSegment,
        isSingleTraveler: isSingleTraveler,
        onRemoveSeat: onRemoveSeat,
        selectedSeat: selectedSeat,
        setTravelerButtonWithRemoveLink: setTravelerButtonWithRemoveLink,
        showRemove: showRemove,
        travelerId: traveler.id
      })]
    })
  }, traveler.id);
};
export default SingleTravelerButton;
//# sourceMappingURL=SingleTravelerButton.js.map